import type { Session, User } from '@supabase/supabase-js'
import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from 'react'
import { isRemovedAccountError } from '../lib/accountRemoval'
import { fetchProfile } from '../lib/api'
import { supabase } from '../lib/supabase'
import type { Profile } from '../types/domain'

interface AuthValue {
  session: Session | null
  user: User | null
  profile: Profile | null
  accountRemoved: boolean
  acknowledgeAccountRemoval: () => void
  revalidateSession: () => void
  loading: boolean
  requestMagicLink: (email: string, redirectTo: string) => Promise<void>
  verifyEmailOtp: (email: string, token: string) => Promise<void>
  signOut: () => Promise<void>
  refreshProfile: () => Promise<void>
}

const AuthContext = createContext<AuthValue | null>(null)

export function AuthProvider({ children }: { children: ReactNode }) {
  const [accountRemoved, setAccountRemoved] = useState(false)
  const removedUserRef = useRef<string | null>(null)
  const [session, setSession] = useState<Session | null>(null)
  const [sessionReady, setSessionReady] = useState(false)
  const [profileSyncRequest, setProfileSyncRequest] = useState(0)
  const [loadedProfile, setLoadedProfile] = useState<{ userId: string | null; value: Profile | null }>({ userId: null, value: null })
  const authGenerationRef = useRef(0)
  const profileRequestRef = useRef(0)
  const currentUserId = session?.user.id ?? null
  const currentUserIdRef = useRef<string | null>(currentUserId)
  currentUserIdRef.current = currentUserId

  const terminateRemovedAccount = useCallback(async (userId: string) => {
    if (currentUserIdRef.current !== userId) return
    removedUserRef.current = userId
    authGenerationRef.current += 1
    profileRequestRef.current += 1
    currentUserIdRef.current = null
    setAccountRemoved(true)
    setLoadedProfile({ userId: null, value: null })
    setSession(null)
    setSessionReady(true)
    // Supabase clears persisted credentials and broadcasts SIGNED_OUT. Its local
    // scope avoids attempting to revoke unrelated sessions from this browser.
    try { await supabase?.auth.signOut({ scope: 'local' }) } catch { /* UI stays signed out if the network fails. */ }
  }, [])

  const requestProfileSync = useCallback(() => {
    if (!currentUserIdRef.current) return
    profileRequestRef.current += 1
    setProfileSyncRequest((request) => request + 1)
  }, [])

  useEffect(() => {
    if (!supabase) {
      setSessionReady(true)
      return
    }

    let active = true
    const applySession = (nextSession: Session | null) => {
      const nextUserId = nextSession?.user.id ?? null
      if (nextUserId && removedUserRef.current === nextUserId) return
      if (nextUserId) setAccountRemoved(false)
      if (currentUserIdRef.current !== nextUserId) profileRequestRef.current += 1
      currentUserIdRef.current = nextUserId
      setSession(nextSession)
    }
    const { data: listener } = supabase.auth.onAuthStateChange((event, nextSession) => {
      if (!active) return
      authGenerationRef.current += 1
      applySession(nextSession)
      // SIGNED_IN also arrives from another tab, even when the user ID is unchanged.
      if (event === 'SIGNED_IN' || event === 'USER_UPDATED' || event === 'TOKEN_REFRESHED') requestProfileSync()
      setSessionReady(true)
    })

    const bootstrapGeneration = authGenerationRef.current
    void (async () => {
      try {
        const { data, error } = await supabase.auth.getSession()
        if (error) throw error
        if (!active || authGenerationRef.current !== bootstrapGeneration) return
        applySession(data.session)
      } catch {
        if (!active || authGenerationRef.current !== bootstrapGeneration) return
        applySession(null)
      } finally {
        if (active && authGenerationRef.current === bootstrapGeneration) setSessionReady(true)
      }
    })()

    return () => {
      active = false
      authGenerationRef.current += 1
      profileRequestRef.current += 1
      listener.subscription.unsubscribe()
    }
  }, [requestProfileSync])

  useEffect(() => {
    const visible = () => { if (document.visibilityState === 'visible') requestProfileSync() }
    window.addEventListener('focus', requestProfileSync)
    window.addEventListener('online', requestProfileSync)
    document.addEventListener('visibilitychange', visible)
    return () => {
      window.removeEventListener('focus', requestProfileSync)
      window.removeEventListener('online', requestProfileSync)
      document.removeEventListener('visibilitychange', visible)
    }
  }, [requestProfileSync])

  useEffect(() => {
    if (!sessionReady) return
    if (!currentUserId) {
      setLoadedProfile((current) => current.userId === null && current.value === null ? current : { userId: null, value: null })
      return
    }

    let active = true
    const request = ++profileRequestRef.current
    // Defer API calls until the Supabase auth notification has fully returned.
    const timer = window.setTimeout(() => {
      void fetchProfile()
        .then((value) => {
          if (active && profileRequestRef.current === request && currentUserIdRef.current === currentUserId) {
            setLoadedProfile({ userId: currentUserId, value })
          }
        })
        .catch((error: unknown) => {
          if (active && profileRequestRef.current === request && currentUserIdRef.current === currentUserId) {
            if (isRemovedAccountError(error)) { void terminateRemovedAccount(currentUserId); return }
            // A failed background request must not discard a known profile.
            // A successful null response still clears it, as does SIGNED_OUT.
            setLoadedProfile((current) => current.userId === currentUserId && current.value
              ? current
              : { userId: currentUserId, value: null })
          }
        })
    }, 0)

    return () => { active = false; window.clearTimeout(timer) }
  }, [currentUserId, profileSyncRequest, sessionReady, terminateRemovedAccount])

  const refreshProfile = useCallback(async () => {
    const userId = currentUserIdRef.current
    if (!userId) {
      setLoadedProfile({ userId: null, value: null })
      return
    }

    const request = ++profileRequestRef.current
    try {
      const value = await fetchProfile()
      if (profileRequestRef.current === request && currentUserIdRef.current === userId) setLoadedProfile({ userId, value })
    } catch (error) {
      if (profileRequestRef.current === request && currentUserIdRef.current === userId && isRemovedAccountError(error)) {
        await terminateRemovedAccount(userId)
        return
      }
      throw error
    }
  }, [terminateRemovedAccount])

  const profile = loadedProfile.userId === currentUserId ? loadedProfile.value : null
  const loading = !sessionReady || (currentUserId !== null && loadedProfile.userId !== currentUserId)

  const value = useMemo<AuthValue>(() => ({
    accountRemoved,
    acknowledgeAccountRemoval: () => setAccountRemoved(false),
    revalidateSession: requestProfileSync,
    session,
    user: session?.user ?? null,
    profile,
    loading,
    async requestMagicLink(email, redirectTo) {
      if (!supabase) throw new Error('Collega Supabase per accedere.')
      const { error } = await supabase.auth.signInWithOtp({
        email,
        options: { shouldCreateUser: true, emailRedirectTo: redirectTo },
      })
      if (error) throw error
    },
    async verifyEmailOtp(email, token) {
      if (!supabase) throw new Error('Collega Supabase per accedere.')
      const { error } = await supabase.auth.verifyOtp({ email, token, type: 'email' })
      if (error) throw error
    },
    async signOut() {
      if (!supabase) return
      const { error } = await supabase.auth.signOut()
      if (error) throw error
    },
    refreshProfile,
  }), [accountRemoved, loading, profile, refreshProfile, requestProfileSync, session])

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

// eslint-disable-next-line react-refresh/only-export-components
export function useAuth() {
  const context = useContext(AuthContext)
  if (!context) throw new Error('useAuth deve essere usato dentro AuthProvider')
  return context
}
